import React, { useState, useEffect } from "react";
import logo from "../../assets/images/logo-rewind.png";
import { useTranslation } from "react-i18next";
import { AddToProjectModalProps } from "../../types/screensTypes";
import { DropdownContent } from "../../types/types";
import { getProjects } from "../../api";
import Dropdown from "../common/Dropdown";
import TextInput from "../ui/TextInput";
import Button from "../ui/Button";
import studio from "../../components/modals/StudioModal.module.css";
import { ModalPortal } from "./ModalPortal";

const AddToProjectModal: React.FC<AddToProjectModalProps> = ({ isOpen, onClose, onAddToProject, placeholder }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [newProjectName, setNewProjectName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true); // Loading state for projects
    const [error, setError] = useState<string | null>(null); // Error state for API call
    const token = localStorage.getItem("token"); // Retrieve token (or other logic to get it)
    const [projects, setProjects] = useState<DropdownContent[]>([]); // State to store fetched projects

    // Add the "Create a new project" option
    const projectOptions: DropdownContent[] = [{ value: "new", label: t("createNewProject") }, ...projects];

    const handleAddToProject = () => {
        if (selectedProject || newProjectName) {
            onAddToProject(selectedProject, newProjectName);
            onClose();
        } else {
            alert(t("warningSelectProject"));
        }
    };

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                if (!token) {
                    setError(t("noToken"));
                    return;
                }
                const response = await getProjects({ token });
                if (!response) {
                    setError(t("failedToFetchProjects"));
                } else {
                    // Map API data to fit the expected structure for rendering
                    const projectsList: DropdownContent[] = response.map((project: { id: string; name: string }) => ({
                        label: project.name,
                        value: project.id,
                    }));

                    setProjects(projectsList);
                }
            } catch (err) {
                setError(t("errorFetchingProjects"));
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    if (!isOpen) return null;

    const modaleTitle = placeholder && placeholder !== "" ? placeholder : t("addThisTextToAProject");

    return (
        <ModalPortal>
            <div className={studio.overlayTop}>
                <div className={studio.projectModalContent}>
                    <div className={studio.logoWrap}>
                        <img src={logo} alt="Logo" />
                    </div>
                    <h1 className={studio.projectModalTitle}>{modaleTitle}</h1>

                    {/* Select a project */}
                    <div className={studio.projectSection}>
                        <Dropdown
                            label=""
                            value={selectedProject}
                            options={projectOptions}
                            onChange={(value) => setSelectedProject(value)}
                            placeholder={t("selectProject")}
                            loading={loading}
                            error={!!error}
                            fullWidth
                            isWithoutMargin
                        />
                    </div>

                    {/* Only show the TextInput when "Create a new project" is selected */}
                    {selectedProject === "new" && (
                        <div className={studio.projectSectionNew}>
                            <div className={studio.projectRow}>
                                <TextInput
                                    placeholder={t("nameOfTheNewProject")}
                                    value={newProjectName}
                                    size={"small"}
                                    onChange={(e) => setNewProjectName(e.target.value)}
                                    fullWidth
                                />
                            </div>
                        </div>
                    )}

                    <div className={studio.buttonsCentered}>
                        <Button
                            variant="primary"
                            shape="pill"
                            block
                            label={t("addToProject")}
                            onClick={handleAddToProject}
                        />
                        <Button variant="cancel" shape="pill" block label={t("close")} onClick={onClose} />
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default AddToProjectModal;
